import { Link, Outlet, useLocation } from 'react-router-dom';

import { useAppData } from '@/app-data';
import { useItemPanel } from '@/components/item-panel';
import { useEpics } from '@/features/backlog/epics';
import EpicSidebar, { SIDEBAR_WIDTH } from '@/components/epic-sidebar';
import BoardSwitcher from '@/components/board-switcher';
import GlobalSearch from '@/components/global-search';
import NotificationBell from '@/components/notification-bell';
import UndoRedo from '@/components/undo-redo';
import ViewSwitcher from '@/components/view-switcher';
import IdentityMenu from '@/features/identity/identity-menu';
import PresenceBar from '@/features/realtime/presence-bar';
import { isDesktopApp } from '@/features/identity/desktop-updates';
import { Search } from '@/components/ui/icons';

/** App shell: sticky top bar (logo + board switcher + tab nav) + centered content. */
const AppLayout = () => {
  // While the details panel is pinned it's a persistent sidebar, so inset the content to sit
  // beside it — but only on the backlog views that actually render the dock (the Members page
  // has no dock, and must not reserve an empty gap). The transition matches the dock's slide so
  // the two move as one (reduced-motion users get an instant inset via CSS).
  const { dockVisible, canDock } = useItemPanel();
  const { sidebarOpen } = useEpics();
  const { currentBoard } = useAppData();
  const { pathname } = useLocation();
  // A board VIEW (kanban / table / timeline) reserves the details dock + epic sidebar; the task
  // page (/:slug/:itemKey) and the members page do not. seg2 is undefined on the /:slug default.
  const seg2 = pathname.split('/').filter(Boolean)[1];
  const dockView =
    !!currentBoard && (seg2 === undefined || seg2 === 'kanban' || seg2 === 'table' || seg2 === 'timeline');
  const inset = dockVisible && dockView;
  // The epic sidebar shows on the backlog views on wide screens (it reserves 268px; narrow
  // screens can't spare it, matching the details dock's canDock gate). It reflows the content
  // to the right, coexisting with the right-hand details dock inset.
  const showSidebar = sidebarOpen && dockView && canDock;
  return (
  <div className="min-h-screen bg-ink text-fg">
    <header className="sticky top-0 z-40 border-b border-line bg-ink/80 backdrop-blur">
      <div className="grid h-14 grid-cols-[1fr_auto_1fr] items-center gap-3 px-6">
        <div className="flex items-center gap-3 justify-self-start">
          <Link
            to="/"
            aria-label="Home"
            title="Home"
            className="flex h-8 w-8 items-center justify-center rounded-lg transition-opacity hover:opacity-80"
          >
            <img src="/newton-logo.png" alt="Newton" width={28} height={28} className="h-7 w-7 object-contain" />
          </Link>
          {/* Boards dropdown + history: desktop only (hidden on mobile). */}
          <div className="hidden sm:block">
            <BoardSwitcher />
          </div>
          <div className="hidden sm:block">
            <UndoRedo />
          </div>
        </div>

        {/* Centered cross-board global search (wide screens). */}
        <div className="hidden w-[min(40vw,520px)] justify-self-center md:block">
          <GlobalSearch />
        </div>

        <nav className="flex items-center gap-1 justify-self-end" aria-label="Sections">
          {/* Narrow screens: a compact search entry that opens the full search page. */}
          <Link
            to="/search"
            aria-label="Search"
            className="flex h-9 w-9 items-center justify-center rounded-full text-fg-muted transition-colors hover:bg-layer-strong hover:text-fg md:hidden"
          >
            <Search className="h-[18px] w-[18px]" />
          </Link>
          <ViewSwitcher />
          {/* Presence avatars — renders (with its own flanking separators) only when others are online. */}
          <PresenceBar />
          {/* In the desktop app the bell + account live in the tab strip (see the hidden anchors below). */}
          {isDesktopApp() ? null : (
            <>
              <NotificationBell />
              <IdentityMenu />
            </>
          )}
        </nav>
      </div>
    </header>

    {showSidebar ? <EpicSidebar /> : null}

    <main
      className="content-inset px-6 py-6"
      style={{
        paddingRight: inset ? 'calc(1.5rem + var(--dock-w-eff))' : undefined,
        paddingLeft: showSidebar ? `calc(1.5rem + ${SIDEBAR_WIDTH}px)` : undefined,
        transition: 'padding-right 380ms cubic-bezier(0.22, 1, 0.36, 1), padding-left 380ms cubic-bezier(0.22, 1, 0.36, 1)',
      }}
    >
      <Outlet />
    </main>
  </div>
  );
};

export default AppLayout;
